import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Download, Trash2, QrCode, Table2, ExternalLink, RefreshCw, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TablesManagement = () => {
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMesa, setNewMesa] = useState({ numero: '', nombre: '' });
  const [downloadingAll, setDownloadingAll] = useState(false);

  const fetchMesas = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mesas`);
      if (response.ok) {
        const data = await response.json();
        setMesas(data);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error('Error al cargar las mesas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMesas();
  }, []);

  const handleAddMesa = async () => {
    if (!newMesa.numero) {
      toast.error('El número de mesa es obligatorio');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/mesas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          numero: parseInt(newMesa.numero),
          nombre: newMesa.nombre || null
        })
      });

      if (response.ok) {
        toast.success(`Mesa ${newMesa.numero} creada`);
        setShowAddModal(false);
        setNewMesa({ numero: '', nombre: '' });
        fetchMesas();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear la mesa');
      }
    } catch (error) {
      console.error('Error creating table:', error);
      toast.error('Error al crear la mesa');
    }
  };

  const handleDeleteMesa = async (numero) => {
    if (!window.confirm(`¿Eliminar la mesa ${numero}?`)) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/mesas/${numero}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Mesa ${numero} eliminada`);
        fetchMesas();
      } else {
        toast.error('Error al eliminar la mesa');
      }
    } catch (error) {
      console.error('Error deleting table:', error);
      toast.error('Error al eliminar la mesa');
    }
  };

  const handleDownloadQR = async (numero) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/mesas/${numero}/qr`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mesa_${numero}_qr.png`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`QR de mesa ${numero} descargado`);
      } else {
        toast.error('Error al descargar el QR');
      }
    } catch (error) {
      console.error('Error downloading QR:', error);
      toast.error('Error al descargar el QR');
    }
  };

  const handleDownloadAllQR = async () => {
    setDownloadingAll(true);
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/mesas/qr/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'todas_las_mesas_qr.png';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Todos los QR descargados');
      } else {
        toast.error('Error al descargar los QR');
      }
    } catch (error) {
      console.error('Error downloading all QR:', error);
      toast.error('Error al descargar los QR');
    } finally {
      setDownloadingAll(false);
    }
  };

  const getMenuUrl = (numero) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/mesa/${numero}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-stone-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 pb-24">
      {/* Header - Mobile optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Gestión de Mesas</h2>
          <p className="text-sm text-gray-500">Administra mesas y códigos QR</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleDownloadAllQR}
            disabled={downloadingAll || mesas.length === 0}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 text-xs sm:text-sm"
          >
            {downloadingAll ? (
              <RefreshCw className="animate-spin" size={14} />
            ) : (
              <Download size={14} />
            )}
            <span className="hidden sm:inline">Descargar</span> QRs
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            size="sm"
            className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Añadir</span> Mesa
          </Button>
        </div>
      </div>

      {/* Stats - Mobile optimized */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Table2 className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{mesas.length}</p>
                <p className="text-xs text-gray-500">Mesas activas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <QrCode className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{mesas.length}</p>
                <p className="text-xs text-gray-500">QR disponibles</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {mesas.map((mesa) => (
          <Card key={mesa.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">Mesa {mesa.numero}</CardTitle>
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  Activa
                </Badge>
              </div>
              {mesa.nombre && (
                <p className="text-sm text-gray-500">{mesa.nombre}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* QR Preview placeholder */}
                <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                  <QrCode size={64} className="text-gray-400" />
                </div>
                
                {/* URL */}
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs">
                  <code className="flex-1 truncate text-gray-600">
                    /mesa/{mesa.numero}
                  </code>
                  <a
                    href={getMenuUrl(mesa.numero)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleDownloadQR(mesa.numero)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Download size={14} className="mr-1" />
                    QR
                  </Button>
                  <Button
                    onClick={() => handleDeleteMesa(mesa.numero)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {mesas.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Table2 size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay mesas</h3>
            <p className="text-gray-500 mb-4">Añade tu primera mesa para empezar</p>
            <Button onClick={() => setShowAddModal(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={16} className="mr-2" />
              Añadir Mesa
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Mesa Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Añadir Nueva Mesa</CardTitle>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="numero">Número de mesa *</Label>
                <Input
                  id="numero"
                  type="number"
                  min="1"
                  value={newMesa.numero}
                  onChange={(e) => setNewMesa({ ...newMesa, numero: e.target.value })}
                  placeholder="Ej: 12"
                />
              </div>
              <div>
                <Label htmlFor="nombre">Nombre/Ubicación (opcional)</Label>
                <Input
                  id="nombre"
                  value={newMesa.nombre}
                  onChange={(e) => setNewMesa({ ...newMesa, nombre: e.target.value })}
                  placeholder="Ej: Terraza, Ventana..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleAddMesa}
                >
                  Crear Mesa
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TablesManagement;
